import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelectedLocation } from "../contexts/SelectedLocationContext";
import { Tab } from "../types/Tab";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CatalogObject, Order } from "square";
import {
  createReturnOrder,
  findOrGetItemsByVariations,
  getOrder,
} from "../api";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import Spinner from "../components/Spinner";
import ProductList from "../components/ProductList";
import { useCatalog } from "../contexts/CatalogContext";
import { OrderItem } from "../types/OrderItem";
import OrderItems from "../components/OrderItems";
import ConfirmationModal from "../components/ConfirmationModal";
import OrderWrapper from "../components/OrderWrapper";
import PageContainer, { GapSize } from "../components/PageContainer";

const RETURN_REASONS: Record<string, string> = {
  defective: "Item arrived damaged or defective",
  wrong_item: "Wrong item received",
  not_as_described: "Item not as described",
  changed_mind: "Changed my mind / no longer needed",
  other: "Other",
};

const OrderReturnPage = () => {
  const { addItems, addImages, getImagesByIds, getItemById } = useCatalog();
  const { setDomain, setCategory, setProduct } = useSelectedLocation();
  const { session } = useCustomerAuth();

  useEffect(() => {
    setDomain(Tab.Orders.toString());
    setCategory(null);
    setProduct(null);
  }, [setDomain, setCategory, setProduct]);

  useEffect(() => {
    window.scrollTo(0, 0); // Scrolls to the top
  }, []); // Runs only once when the component mounts

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const searchOrderId: string | null = searchParams.get("id");
  const [order, setOrder] = useState<Order | null>(
    location?.state?.order ?? null,
  );

  const [reason, setReason] = useState<string>("defective");
  const [notes, setNotes] = useState<string>("");

  // The variations of the order
  const orderVariationIds = useMemo(
    () => order?.lineItems?.map((li) => li.catalogObjectId),
    [order],
  );
  const [orderVariations, setOrderVariations] = useState<CatalogObject[]>([]);

  // All Items in the Order
  const [orderItems, setOrderItems] = useState<CatalogObject[]>([]);
  const [itemsToReturn, setItemsToReturn] = useState<OrderItem[]>([]);
  const [isConfirmationModelOpen, setIsConfirmationModelOpen] =
    useState<boolean>(false);

  const handleTrackOrder = useCallback(async () => {
    setIsProcessing(true);
    try {
      // Get Order — ownership-gated, so authorize the lookup the same two ways
      // get-order accepts: the customer JWT when signed in, or the buyer email
      // carried over from the tracking page (or the ?email= query param).
      // The account order page links here with only ?id=, so without the token
      // that arrival 403s and the page renders empty.
      const getOrderResponse = await getOrder(searchOrderId!, {
        email: location?.state?.email ?? searchParams.get("email") ?? undefined,
        authToken: session?.authToken,
      });
      if (getOrderResponse?.data) {
        setOrder(getOrderResponse.data);
      } else {
        alert(
          "We couldn't load that order. Please confirm you're using the email this order was placed with, or sign in.",
        );
      }
      setIsProcessing(false);
    } catch (error) {
      console.error("Get Order failed:", error);
      alert(
        "We couldn't find that order. Please check the order ID and the email used to place it.",
      );
      setIsProcessing(false);
    }
  }, [searchOrderId, location?.state?.email, searchParams, session?.authToken]);

  const handleCreateReturn = async () => {
    setIsProcessing(true);
    try {
      // Create Return Order. Pass the buyer email carried over from the
      // tracking page (or ?email=) so the server can verify order ownership
      // for guests; logged-in users are authorized by their JWT.
      const result = await createReturnOrder(
        order!,
        itemsToReturn,
        reason,
        notes,
        session?.authToken,
        location?.state?.email ?? searchParams.get("email") ?? undefined,
      );
      setIsProcessing(false);
      if (result?.error) {
        alert(
          "We couldn't submit your return request. Please confirm you're using the email this order was placed with, or contact support@americanaandcompany.com.",
        );
        return;
      }
      navigate("/home");
    } catch (error) {
      console.error("Create return failed:", error);
      alert(
        "We were unable to process your return. Please contact support@americanaandcompany.com.",
      );
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    const fetchItems = async () => {
      try {
        // 1. Get and Set Order Variations
        const variations = await findOrGetItemsByVariations(
          (orderVariationIds?.filter(Boolean) ?? []) as string[],
          addItems,
          addImages,
          getImagesByIds,
        );

        if (variations) setOrderVariations(variations);

        // 2. Set all Order Items
        let items: CatalogObject[] = [];
        for (let i = 0; i < (order?.lineItems?.length ?? 0); i++) {
          const item = order!.lineItems![i];
          const variation = (variations ?? []).find(
            (v) => v.id === item.catalogObjectId,
          );
          const variationItemId = variation?.itemVariationData?.itemId;
          const itemObject = getItemById(variationItemId!);
          for (let j = 0; j < Number(item.quantity); j++) {
            if (itemObject) items.push(itemObject);
          }
        }
        setOrderItems(items);
      } catch (error) {
        console.error("Get items failed:", error);
      }
    };

    // Fetch items in order that haven't already been fetched
    if (orderVariations?.length === 0 && (orderVariationIds?.length ?? 0) > 0) {
      fetchItems();
    }
  }, [
    orderVariations,
    orderVariationIds,
    order,
    getItemById,
    addItems,
    addImages,
    getImagesByIds,
  ]);

  useEffect(() => {
    if (!order && !!searchOrderId) {
      handleTrackOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, searchOrderId, handleTrackOrder]);

  return (
    <>
      {isProcessing && <Spinner text="Submitting Return Request..." />}
      <ConfirmationModal
        isOpen={isConfirmationModelOpen}
        onConfirm={() => handleCreateReturn()}
        onCancel={() => setIsConfirmationModelOpen(false)}
      >
        Are you sure you would like to submit this return request?
      </ConfirmationModal>

      <PageContainer hasPadding gapSize={GapSize.Small}>
        <div className="order-return-wrapper">
          {/* Items from Order */}
          <div className="order-return-col">
            <OrderWrapper>
              <div className="order-page-header">
                <h1 className="order-page-title">Return / Refund Request</h1>
                <p className="order-page-subtitle">
                  Click an item to add it to your return request. We&apos;ll
                  review your request and follow up by email &mdash; if a
                  ship-back is needed, we&apos;ll send return instructions once
                  it&apos;s approved.
                </p>
              </div>
              <ProductList
                items={orderItems}
                onClickProduct={(product: CatalogObject) => {
                  setItemsToReturn((prevItems) => {
                    const pi = prevItems.find(
                      (i) => i.product.id === product.id,
                    );
                    return [
                      ...prevItems.filter((i) => i.product.id !== product.id),
                      {
                        product: product,
                        quantity: (pi?.quantity ?? 0) + 1,
                        variation: orderVariations?.find(
                          (ov) => ov.itemVariationData?.itemId === product.id,
                        ) as CatalogObject,
                      },
                    ];
                  });
                  setOrderItems((prevItems) => {
                    const pi = prevItems.findIndex((i) => i.id === product.id);
                    return prevItems.filter((_, i) => i !== pi);
                  });
                }}
              />
            </OrderWrapper>
          </div>

          {/* Items to Return + reason */}
          <div className="order-return-col">
            <OrderWrapper>
              <div className="order-page-header">
                <h1 className="order-page-title">Items to Return</h1>
                <p className="order-page-subtitle">
                  Review the items and tell us why you&apos;re returning them.
                </p>
              </div>
              <OrderItems orderItems={itemsToReturn} isReturn />

              {/* Reason */}
              <div className="flex flex-col gap-1 mt-3">
                <label className="text-sm font-semibold text-brown-dark opensans-text">
                  Reason for return
                </label>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="order-input-field"
                >
                  {Object.entries(RETURN_REASONS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Notes */}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-brown-dark opensans-text">
                  Additional details{" "}
                  <span className="font-normal text-gray-500">(optional)</span>
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value.slice(0, 1000))}
                  className="order-input-field min-h-[80px] resize-y"
                  placeholder="Describe the issue, attach order photos if needed, etc."
                />
              </div>

              <button
                onClick={() => setIsConfirmationModelOpen(true)}
                disabled={itemsToReturn.length === 0}
                className="order-action-btn order-action-btn--danger w-full disabled:opacity-50"
              >
                Submit Return Request
              </button>
            </OrderWrapper>
          </div>
        </div>
      </PageContainer>
    </>
  );
};

export default OrderReturnPage;
